<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="mb-4 detail-page-header">
        <button type="button" class="btn btn-link text-decoration-none detail-page-back" @click="$router.go(-1)" style="padding: 10px 0">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" />{{$t('back')}}
        </button>
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 detail-page-title-row">
          <h3 class="mb-0 detail-page-title">{{ user.name }}</h3>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- User Info -->
      <div v-else-if="user && user.id">
        <!-- Tabs -->
        <ul class="nav nav-tabs detail-page-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'overview'}" @click="step = 'overview'">{{$t('overview')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_inventories') != -1">
            <a class="nav-link" :class="{active: step === 'products'}" @click="step = 'products'">{{$t('products')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_clients') != -1">
            <a class="nav-link" :class="{active: step === 'clients'}" @click="step = 'clients'">{{$t('clients')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_quotes') != -1">
            <a class="nav-link" :class="{active: step === 'quote'}" @click="step = 'quote'">{{$t('quote')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_orders') != -1">
            <a class="nav-link" :class="{active: step === 'orders'}" @click="step = 'orders'">{{$t('orders')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_deliveries') != -1">
            <a class="nav-link" :class="{active: step === 'deliveries'}" @click="step = 'deliveries'">{{$t('deliveries')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_invoices') != -1">
            <a class="nav-link" :class="{active: step === 'invoices'}" @click="step = 'invoices'">{{$t('invoices')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_returns') != -1">
            <a class="nav-link" :class="{active: step === 'returns'}" @click="step = 'returns'">{{$t('returns')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_payments') != -1">
            <a class="nav-link" :class="{active: step === 'payments'}" @click="step = 'payments'">{{$t('payments')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_attachments') != -1">
            <a class="nav-link" :class="{active: step === 'attachments'}" @click="step = 'attachments'">{{$t('attachments')}}</a>
          </li>
        </ul>

        <!-- Tab Content -->
        <!-- Overview Tab -->
        <div v-if="step === 'overview'">
          <div class="row">
            <!-- Left Column: User Details -->
            <div class="col-md-8">
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-person" class="bi bi-person me-2" />{{$t('user')}} {{$t('information')}}</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('reference')}}</small>
                      <strong>#{{shortId(user.id)}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('name')}}</small>
                      <strong>{{user.name}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('email')}}</small>
                      <strong>{{user.email || '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('phone')}}</small>
                      <strong>{{user.phone || '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('role')}}</small>
                      <span class="badge bg-primary text-uppercase">{{user.role}}</span>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('created_at')}}</small>
                      <strong>{{formatDate(user.created_at)}}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Permissions Card -->
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-shield-lock" class="bi bi-shield-lock me-2" />{{$t('permissions')}}</h6>
                </div>
                <div class="card-body">
                  <div class="row g-3">
                    <template v-for="module in permissionsModules">
                      <div v-if="moduleHasPermissions(module)" :key="module.id" class="col-md-6">
                        <div class="card border shadow-sm h-100">
                          <div class="card-header py-2" :style="{ backgroundColor: module.color + '15', borderBottom: '1px solid ' + module.color + '30' }">
                            <h6 class="mb-0" :style="{ color: module.color }">
                              <AppIcon :name="module.icon || 'bi bi-folder'" :class="module.icon || 'bi bi-folder'" class="me-2" />
                              {{$t(module.title)}}
                            </h6>
                          </div>
                          <div class="card-body p-3">
                            <div class="d-flex flex-wrap gap-2">
                              <template v-for="item in module.items">
                                <template v-for="permission in Object.values(item)[0].permissions">
                                  <span v-if="user.permissions && user.permissions.includes(permission.key)" :key="permission.key" 
                                        class="badge bg-success" 
                                        style="font-size: 10px; font-weight: 500;">
                                    <AppIcon name="bi-check-circle" class="bi bi-check-circle me-1" />
                                    {{$t(permission.label)}}
                                  </span>
                                </template>
                              </template>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column -->
            <div class="col-md-4">
              <!-- Access Summary Card -->
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-key" class="bi bi-key me-2" />{{$t('access_summary')}}</h6>
                </div>
                <div class="card-body">
                  <div class="d-flex align-items-center mb-3">
                    <div class="avatar-circle bg-light me-3 d-flex align-items-center justify-content-center" style="width: 50px; height: 50px; border-radius: 50%;">
                      <AppIcon name="bi-person-badge" class="bi bi-person-badge text-primary" style="font-size: 24px;" />
                    </div>
                    <div>
                      <h5 class="mb-0">{{ user.role === 'owner' ? $t('full_access') : (user.role === 'admin' ? $t('admin_access') : $t('limited_access')) }}</h5>
                      <small class="text-muted text-uppercase">{{ user.role }}</small>
                    </div>
                  </div>
                  <p class="small text-muted mb-0">
                    {{ user.role === 'owner' ? $t('full_access_description') : (user.role === 'admin' ? $t('admin_access_description') : $t('limited_access_description')) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Other Tabs -->
        <div v-if="step === 'products' && helper.checkAccess('view_inventories') != -1">
          <product-component v-if="user && user.id" :user_id="user.id" :user="user" :isDetailClient="true"></product-component>
        </div>

        <div v-if="step === 'clients' && helper.checkAccess('view_clients') != -1">
          <client-component v-if="user && user.id" :user_id="user.id" :user="user" :isDetailClient="true"></client-component>
        </div>

        <div v-if="step === 'quote' && helper.checkAccess('view_quotes') != -1">
          <quote-component v-if="user && user.id" :user_id="user.id" :user="user" :isDetailClient="true"></quote-component>
        </div>

        <div v-if="step === 'orders' && helper.checkAccess('view_orders') != -1">
          <order-component v-if="user && user.id" :user_id="user.id" :user="user" :isDetailClient="true"></order-component>
        </div>

        <div v-if="step === 'deliveries' && helper.checkAccess('view_deliveries') != -1">
          <delivery-component v-if="user && user.id" :user_id="user.id" :user="user" :isDetailClient="true"></delivery-component>
        </div>

        <div v-if="step === 'invoices' && helper.checkAccess('view_invoices') != -1">
          <invoice-component v-if="user && user.id" :user_id="user.id" :user="user" :isDetailClient="true"></invoice-component>
        </div>

        <div v-if="step === 'returns' && helper.checkAccess('view_returns') != -1">
          <return-component v-if="user && user.id" :user_id="user.id" :user="user" :isDetailClient="true"></return-component>
        </div>

        <div v-if="step === 'payments' && helper.checkAccess('view_payments') != -1">
          <payment-component v-if="user && user.id" :user_id="user.id" :isDetailSupplier="true"></payment-component>
        </div>

        <div v-if="step === 'attachments' && helper.checkAccess('view_attachments') != -1">
          <attachments :entity_id="user.id" :user_id="user.id" entity_type="user" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import helper from '../../helper'
import ProductComponent from '../product/Product.vue'
import ClientComponent from '../client/Client.vue'
import OrderComponent from '../../components/OrderComponent.vue'
import QuoteComponent from '../../components/QuoteComponent.vue'
import InvoiceComponent from '../../components/InvoiceComponent.vue'
import DeliveryComponent from '../../components/DeliveryComponent.vue'
import ReturnComponent from '../../components/ReturnComponent.vue'
import PaymentComponent from '../../components/PaymentComponent.vue'
import Attachments from '../../components/Attachment.vue'

export default {
  name: 'UserDetails',
  components :{
    ProductComponent,
    ClientComponent,    
    OrderComponent,
    QuoteComponent,
    InvoiceComponent,
    DeliveryComponent,
    ReturnComponent,
    PaymentComponent,
    Attachments
  },
  data(){
    return {
        user : {},
        total : 0,
        step : 'overview',
        amount_paid : 0,
        total_orders : 0,
        total_quote : 0,
        total_deliveries : 0,
        total_invoices : 0,
        loading: false,
        permissionsModules: [
            {
                id: 'dashboard',
                title: 'dashboard_title',
                icon: 'bi bi-speedometer2',
                color: '#6366f1',
                items: [
                    {
                        dashboard: { 
                            permissions: [
                                { key: 'view_dashboard', label: 'view_dashboard' },
                            ]
                        }
                    }
                ]
            },
            {
                id: 'crm',
                title: 'crm_title',
                icon: 'bi bi-people-fill',
                color: '#10b981',
                items: [
                    {
                        clients: {
                            permissions: [
                                { key: 'create_clients', label: 'create_clients' },
                                { key: 'edit_clients', label: 'edit_clients' },
                                { key: 'view_clients', label: 'view_clients' },
                                { key: 'delete_clients', label: 'delete_clients' }
                            ]
                        }
                    },
                    {
                        suppliers: {
                            permissions: [
                                { key: 'create_suppliers', label: 'create_suppliers' },
                                { key: 'edit_suppliers', label: 'edit_suppliers' },
                                { key: 'view_suppliers', label: 'view_suppliers' },
                                { key: 'delete_suppliers', label: 'delete_suppliers' }
                            ]
                        }
                    }
                ]
            },
            {
                id: 'inventory',
                title: 'inventory_title',
                icon: 'bi bi-box-seam',
                color: '#f59e0b',
                items: [
                    {
                        products: {
                            permissions: [
                                { key: 'create_inventories', label: 'create_inventories' },
                                { key: 'edit_inventories', label: 'edit_inventories' },
                                { key: 'view_inventories', label: 'view_inventories' },
                                { key: 'delete_inventories', label: 'delete_inventories' }
                            ]
                        }
                    },
                    {
                      categories: {
                        permissions: [
                          { key: 'create_categories', label: 'create_categories' },
                          { key: 'edit_categories', label: 'edit_categories' },
                          { key: 'view_categories', label: 'view_categories' },
                          { key: 'delete_categories', label: 'delete_categories' }
                        ]
                      }
                    },
                    {
                      warehouses: {
                        permissions: [
                          { key: 'create_warehouses', label: 'create_warehouses' },
                          { key: 'edit_warehouses', label: 'edit_warehouses' },
                          { key: 'view_warehouses', label: 'view_warehouses' },
                          { key: 'delete_warehouses', label: 'delete_warehouses' }
                        ]
                      }
                    }
                ]
            },
            {
                id: 'sales',
                title: 'sales_title',
                icon: 'bi bi-cart-check-fill',
                color: '#3b82f6',
                items: [
                    {
                        quote: {
                            permissions: [
                                { key: 'create_quote', label: 'create_quote' },
                                { key: 'edit_quote', label: 'edit_quote' },
                                { key: 'view_quotes', label: 'view_quotes' },
                                { key: 'delete_quote', label: 'delete_quote' }
                            ]
                        }
                    },
                    {
                        orders: {
                            permissions: [
                                { key: 'create_orders', label: 'create_orders' },
                                { key: 'edit_orders', label: 'edit_orders' },
                                { key: 'view_orders', label: 'view_orders' },
                                { key: 'delete_orders', label: 'delete_orders' }
                            ]
                        }
                    },
                    {
                        deliveries: {
                            permissions: [
                                { key: 'create_deliveries', label: 'create_deliveries' },
                                { key: 'edit_deliveries', label: 'edit_deliveries' },
                                { key: 'view_deliveries', label: 'view_deliveries' },
                                { key: 'delete_deliveries', label: 'delete_deliveries' }
                            ]
                        }
                    },
                    {
                        invoices: {
                            permissions: [
                                { key: 'create_invoices', label: 'create_invoices' },
                                { key: 'edit_invoices', label: 'edit_invoices' },
                                { key: 'view_invoices', label: 'view_invoices' },
                                { key: 'delete_invoices', label: 'delete_invoices' }
                            ]
                        }
                    },
                    {
                        processes: {
                            permissions: [
                                { key: 'create_receipts', label: 'create_receipts' },
                                { key: 'edit_receipts', label: 'edit_receipts' },
                                { key: 'view_receipts', label: 'view_receipts' },
                                { key: 'delete_receipts', label: 'delete_receipts' }
                            ]
                        }
                    },
                    {
                      returns: {
                        permissions: [
                          { key: 'create_returns', label: 'create_returns' },
                          { key: 'edit_returns', label: 'edit_returns' },
                          { key: 'view_returns', label: 'view_returns' },
                          { key: 'delete_returns', label: 'delete_returns' }
                        ]
                      }
                    }
                ]
            },
            {
              id: 'purchases',
              title: 'purchasing_title',
              icon: 'bi bi-bag-check-fill',
              color: '#8b5cf6',
              items: [
                {
                  purchases: {
                    permissions: [
                      { key: 'create_purchases', label: 'create_purchases' },
                      { key: 'edit_purchases', label: 'edit_purchases' },
                      { key: 'view_purchases', label: 'view_purchases' },
                      { key: 'delete_purchases', label: 'delete_purchases' }
                    ]
                  }
                },
                {
                  receptions: {
                    permissions: [
                      { key: 'create_receptions', label: 'create_receptions' },
                      { key: 'edit_receptions', label: 'edit_receptions' },
                      { key: 'view_receptions', label: 'view_receptions' },
                      { key: 'delete_receptions', label: 'delete_receptions' }
                    ]
                  }
                },
                {
                  "supplier_invoices": {
                    permissions: [
                      { key: 'create_supplier_invoices', label: 'create_supplier_invoices' },
                      { key: 'edit_supplier_invoices', label: 'edit_supplier_invoices' },
                      { key: 'view_supplier_invoices', label: 'view_supplier_invoices' },
                      { key: 'delete_supplier_invoices', label: 'delete_supplier_invoices' }
                    ]
                  }
                },
                {
                  "supplier_returns": {
                    permissions: [
                      { key: 'create_supplier_returns', label: 'create_supplier_returns' },
                      { key: 'edit_supplier_returns', label: 'edit_supplier_returns' },
                      { key: 'view_supplier_returns', label: 'view_supplier_returns' },
                      { key: 'delete_supplier_returns', label: 'delete_supplier_returns' }
                    ]
                  }
                }
              ]
            },
            {
              id: 'pos',
              title: 'pos_title',
              icon: 'bi bi-cash-stack',
              color: '#ec4899',
              items: [
                {
                  pos: {
                    permissions: [
                      { key: 'create_pos', label: 'create_pos' },
                      { key: 'view_pos', label: 'view_pos' },
                    ]
                  }
                },
                {
                  "transaction": {
                    permissions: [
                      { key: 'view_pos_transactions', label: 'view_pos_transactions' },
                      { key: 'delete_pos_transactions', label: 'delete_pos_transactions' }
                    ]
                  }
                }
              ]
            },
            {
              id: 'finance',
              title: 'finance_title',
              icon: 'bi bi-wallet2',
              color: '#14b8a6',
              items: [
                {
                  payments: {
                    permissions: [
                      { key: 'create_payments', label: 'create_payments' },
                      { key: 'view_payments', label: 'view_payments' },
                      { key: 'delete_payments', label: 'delete_payments' }
                    ]
                  }
                }
              ]
            },
            {
                id: 'attachments',
                title: 'attachments_title',
                icon: 'bi bi-paperclip',
                color: '#64748b',
                items: [
                    {
                        attachments: {
                            permissions: [
                                { key: 'create_attachments', label: 'create_attachments' },
                                { key: 'edit_attachments', label: 'edit_attachments' },
                                { key: 'view_attachments', label: 'view_attachments' },
                                { key: 'delete_attachments', label: 'delete_attachments' }
                            ]
                        }
                    }
                ]
            },
            {
                id: 'leads',
                title: 'leads_title',
                icon: 'bi bi-person-lines-fill',
                color: '#0ea5e9',
                items: [
                    {
                        leads: {
                            permissions: [
                                { key: 'create_leads', label: 'create_leads' },
                                { key: 'edit_leads', label: 'edit_leads' },
                                { key: 'view_leads', label: 'view_leads' },
                                { key: 'delete_leads', label: 'delete_leads' }
                            ]
                        }
                    }
                ]
            },
            {
                id: 'marketing',
                title: 'marketing_title',
                icon: 'bi bi-megaphone',
                color: '#6366f1',
                items: [
                    {
                        marketing: {
                            permissions: [
                                { key: 'view_marketing', label: 'view_marketing' },
                                { key: 'manage_marketing', label: 'manage_marketing' }
                            ]
                        }
                    }
                ]
            }
        ]
    }
  },
  created(){
    if(!this.$route.params || !this.$route.params.id) return this.$route.push('/users')
    this.getUser(this.$route.params.id)
  },
  mounted(){
    window.mixpanel.track("user_details");
  },
    methods : {
        async getUser(id){
          try{
              this.loading = true
              const response = await helper.customGet(`${process.env.VUE_APP_API}/users/${id}`)
              this.user = response.data
              this.loading = false
              
          }catch(error){
              this.$router.push({path : '/'})
              this.loading = false
          }
        },
        moduleHasPermissions(module) {
          if (!this.user.permissions) return false;
          return module.items.some(item => {
            return Object.values(item)[0].permissions.some(p => this.user.permissions.includes(p.key));
          });
        },
        formatDate(date) { return helper.formatDate(date) },
        shortId(id) { return helper.shortId(id) },
        formatMoney(number) { return helper.formatMoney(number);}
    },
    computed : {
        helper() { return helper },
        business(){ return this.$store.getters.BUSINESS},
    }
}
</script>

<style scoped>
.avatar-circle {
  transition: transform 0.3s ease;
}
.avatar-circle:hover {
  transform: scale(1.05);
}
.card {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  border: none;
}
.card-header {
  border-bottom: 1px solid rgba(0,0,0,0.05);
}
.last-child-no-border:last-child {
  border-bottom: none !important;
}
</style>
